import { memo, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { UIProductCard } from 'blocks/product-card';
import { UIButton } from 'ui/button';
import { cn } from 'ui/lib/utils';
import { useAuth } from 'context/AuthContext';
import { useShoppingCart } from 'context/ShoppingCartContext';
import { useEffectivePrice } from 'context/StoreSaleContext';
import ProductSchema from '@c/schema/ProductSchema';
import ProtectionPolicyModal from '@c/modals/ProtectionPolicyModal';
import SafeImage from '@ui/SafeImage';
import { AffirmTag } from '@c/affirm/AffirmTag';
import { GavelIcon } from '@c/products/AuctionBanner';
import { getHighestBid } from '@util/firestore/bid/bid.service';
import { getMasterById } from '@util/firestore/master/master.service';
import { formatProductItems, trackEvent } from '@util/analytics';
import { useListId } from '@util/hooks/useListId';
import useCountdown from '@util/hooks/useCountdown';
import { formatCurrency, getCartItemFromProductDocument } from '@util/index';
import { getProductUrl } from '@util/urlHelpers';
import { ProductDocument } from '@util/types/firestore/products';
import {
    hasFreeShipping,
    hasQuotableShipping,
    isSoldOut,
    toProductCardProps,
} from './productCardProps';

// ── List-variant commerce ──────────────────────────────────────────────────
// The design-system list card carries more than the grid card — an image
// carousel, financing, an explicit shipping price, Add-to-Cart, and a
// buyer-protection note (restored to parity with the legacy ListProductCard per
// the homepage review's B1 ruling). Each piece is its own component so the grid
// path never mounts cart/auth subscriptions or modal state it doesn't need.

// Past this many images, per-image dots stop being readable and stop fitting.
// Show a position counter instead of five dots that cannot represent the set —
// the old `i === safeIndex % 5` lit dot 1 for image 6, dot 2 for image 7, and
// so on, actively misreporting where the shopper was.
const MAX_DOTS = 5;

// Horizontal travel (px) that counts as a swipe rather than a tap, and the
// vertical slack allowed within it. Below the first, the gesture falls through
// to the card's link, which is what a tap should do.
const SWIPE_MIN_X = 40;
const SWIPE_MAX_Y = 60;

/**
 * Image carousel fitted to the card's image area. (The legacy
 * PlainProductCarousel hardcodes ~50rem widths that blow out the design system's
 * ~200px image column.) Arrows page through the images, as does a horizontal
 * swipe; the image itself stays part of the card's single link target.
 */
function ListCardCarousel({ product }: { product: ProductDocument }) {
    const images = product.images?.length
        ? product.images
        : product.thumbnail
          ? [{ full: product.thumbnail, thumb: product.thumbnail }]
          : [];
    const [index, setIndex] = useState(0);
    // Written during the gesture, so a ref rather than state — re-rendering on
    // every touchmove would fight the browser's own scrolling.
    const touchStart = useRef<{ x: number; y: number } | null>(null);
    const swiped = useRef(false);
    if (!images.length) return null;
    const safeIndex = index % images.length;
    const go = (dir: number) =>
        setIndex((i) => (i + dir + images.length) % images.length);
    const step = (dir: number) => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        go(dir);
    };

    // Swipe, restored: the carousel this replaced supported drag, and on the
    // surface carrying ~65% of revenue the two arrows were the only way through
    // the images. Handled here rather than with a library so the card's link
    // still receives ordinary taps.
    const onTouchStart = (e: React.TouchEvent) => {
        const t = e.touches[0];
        touchStart.current = { x: t.clientX, y: t.clientY };
        swiped.current = false;
    };
    const onTouchMove = (e: React.TouchEvent) => {
        const start = touchStart.current;
        if (!start || swiped.current || images.length < 2) return;
        const t = e.touches[0];
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;
        if (Math.abs(dx) < SWIPE_MIN_X || Math.abs(dy) > SWIPE_MAX_Y) return;
        swiped.current = true;
        go(dx < 0 ? 1 : -1);
    };
    const onTouchEnd = (e: React.TouchEvent) => {
        // A completed swipe must not also open the listing.
        if (swiped.current) {
            e.preventDefault();
            e.stopPropagation();
        }
        touchStart.current = null;
    };

    return (
        <div
            className="absolute inset-0 h-full w-full"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
        >
            <SafeImage
                src={images[safeIndex].full}
                alt={product.title}
                fill
                className="h-full w-full object-cover"
                loading="lazy"
            />
            {images.length > 1 && (
                <>
                    {/* 44px tap targets (Apple HIG / Material), with the visible
                        circle kept at 26px so the card art is not swamped. */}
                    <button
                        type="button"
                        aria-label="Previous image"
                        onClick={step(-1)}
                        className="absolute left-0 top-1/2 z-[3] flex size-[44px] -translate-y-1/2 items-center justify-center text-white"
                    >
                        <span className="flex size-[26px] items-center justify-center rounded-full bg-black/40 transition-colors hover:bg-black/60">
                            <ChevronLeft size={16} aria-hidden="true" />
                        </span>
                    </button>
                    <button
                        type="button"
                        aria-label="Next image"
                        onClick={step(1)}
                        className="absolute right-0 top-1/2 z-[3] flex size-[44px] -translate-y-1/2 items-center justify-center text-white"
                    >
                        <span className="flex size-[26px] items-center justify-center rounded-full bg-black/40 transition-colors hover:bg-black/60">
                            <ChevronRight size={16} aria-hidden="true" />
                        </span>
                    </button>
                    <div className="pointer-events-none absolute inset-x-0 bottom-[8px] z-[3] flex justify-center gap-[4px]">
                        {images.length <= MAX_DOTS ? (
                            images.map((_, i) => (
                                <span
                                    key={i}
                                    className={cn(
                                        'size-[6px] rounded-full',
                                        i === safeIndex ? 'bg-white' : 'bg-white/50'
                                    )}
                                />
                            ))
                        ) : (
                            <span className="rounded-full bg-black/50 px-[8px] py-[2px] text-[11px] font-medium leading-tight text-white">
                                {safeIndex + 1} / {images.length}
                            </span>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

/**
 * Add-to-cart for the list card, carrying the two guards the legacy
 * ListProductCard had and the consolidation dropped (GB #1323):
 *
 *  - `item.stock` is passed to addGuestCartItem. Both the guest stock cap and
 *    its ADD_FAILED / stock_cap telemetry sit behind `stock !== undefined`, so
 *    omitting the argument silently uncapped guest adds AND took a money-path
 *    detector off exactly the guest surfaces it was added to cover.
 *  - a re-entrancy guard, so rapid taps can't queue duplicate writes. It is a
 *    ref, not the isAdding state: on the guest path the whole handler runs in
 *    one synchronous tick, and a state flag set and cleared within that tick
 *    never reads back as true. The state exists only to drive the label.
 *
 * The context owns the toast, the analytics and opening the cart, and opens it
 * only on a successful add — so this must not call setCartOpen on the guest
 * path, or a cap-blocked add still slides the cart open and reads as success.
 *
 * Auth has THREE states, not two: signed out (`!user`), signed in with the
 * profile loaded (`userDoc`), and signed in while the profile is still loading
 * (`isLoadingUserDoc`). The third one used to match neither branch, so the
 * button flipped to "Adding…" and then back with nothing added and no error —
 * a silent no-op on the money path. Disable it while the profile is in flight.
 */
function AddToCartButton({ item }: { item: ProductDocument }) {
    const salePrice = useEffectivePrice(item);
    const { user, userDoc, isLoadingUserDoc } = useAuth();
    const { increaseCartQty, canAddProductToCart, setCartOpen, addGuestCartItem } =
        useShoppingCart();
    const [isAdding, setIsAdding] = useState(false);
    const addInFlight = useRef(false);
    const soldOut = isSoldOut(item);
    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (addInFlight.current) return;
        // Belt and braces: the button is disabled in this state, but a
        // programmatic click must not fall through all three branches.
        if (isLoadingUserDoc) return;
        const cartItem = getCartItemFromProductDocument(item, salePrice ?? undefined);
        if (!cartItem) return;
        addInFlight.current = true;
        setIsAdding(true);
        try {
            if (userDoc) {
                if (canAddProductToCart(item.id)) await increaseCartQty(cartItem);
                else setCartOpen(true);
            } else if (!user) {
                addGuestCartItem(cartItem, item.stock);
            }
        } finally {
            addInFlight.current = false;
            setIsAdding(false);
        }
    };
    const label = soldOut
        ? 'Sold out'
        : isLoadingUserDoc
          ? 'Loading…'
          : isAdding
            ? 'Adding…'
            : 'Add to cart';
    return (
        <UIButton
            variant="primary"
            size="md"
            block
            disabled={soldOut || isAdding || isLoadingUserDoc}
            onClick={handleClick}
        >
            {label}
        </UIButton>
    );
}

/**
 * An auction is not buyable through the ordinary cart. Two separate problems
 * came out of the Aug 2026 review, both fixed by never rendering Add-to-cart on
 * an auction listing:
 *
 *  1. The card shows the current highest bid in the price slot but the cart adds
 *     the item at `item.price` (the seller's listed price) — the shopper is
 *     charged something other than what they were shown.
 *  2. Worse: nothing in the cart conversion checks that the buyer is the winning
 *     bidder, so any visitor could put an auction into normal checkout and buy
 *     it outright, bypassing bidding entirely.
 *
 * The action sends the shopper to the listing, where the real bid flow lives.
 * A router push rather than an anchor: UIButton renders a <button> and an
 * anchor inside one is invalid, and the card itself is already a crawlable
 * link to this same URL, so nothing is lost.
 */
function ViewAuctionButton({ item, ended }: { item: ProductDocument; ended: boolean }) {
    const router = useRouter();
    return (
        <UIButton
            variant="primary"
            size="md"
            block
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void router.push(getProductUrl(item.slug));
            }}
        >
            {ended ? 'View auction result' : 'View auction & bid'}
        </UIButton>
    );
}

/**
 * "Have a similar item? Sell yours." — a seller-acquisition entry point the
 * legacy ListProductCard carried on every search result row, and which the card
 * consolidation dropped. Restored per Bill (Aug 2026). Same behaviour as
 * before: sm and up only, and it targets create-listing for activated sellers.
 */
function SellYoursNote() {
    const { userDoc } = useAuth();
    return (
        <p className="hidden gap-[4px] text-[12px] text-gray-600 sm:flex">
            Have a similar item?{' '}
            <Link
                target="_blank"
                title="Sell your used camera gear on GearFocus"
                href={userDoc?.account_activated ? '/sell/create-listing' : '/sell'}
                className="font-semibold underline"
                onClick={(e) => e.stopPropagation()}
            >
                Sell yours.
            </Link>
        </p>
    );
}

/** Buyer-protection line + its policy modal (list card only). */
function BuyerProtectionNote() {
    const [open, setOpen] = useState(false);
    return (
        <>
            <p className="text-[11px] leading-snug text-gray-500">
                Shop with confidence.{' '}
                <button
                    type="button"
                    className="text-info underline"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpen(true);
                    }}
                >
                    GearFocus Buyer Protection
                </button>{' '}
                is included with every purchase.
            </p>
            <ProtectionPolicyModal isOpen={open} dismiss={() => setOpen(false)} />
        </>
    );
}

/**
 * Explicit shipping line for the list card — paid shipping must be visible
 * (the grid card only surfaces a free badge).
 *
 * Every branch here has to be something checkout will actually bill:
 *
 *  - Not a flat-rate listing → checkout quotes a live carrier rate, so no number
 *    on this card can be right. Say so rather than printing the seller's unused
 *    flat table (hasQuotableShipping).
 *  - Flat-rate and free to every US destination → "Free shipping".
 *  - Flat-rate, free to the lower 48 but charging AK/HI/PR → qualify it. A
 *    sample of 1,000 live listings found 104 in exactly this state, and the
 *    unqualified "Free shipping" was a promise checkout broke for those buyers.
 *  - Flat-rate with a continental-US cost → show it.
 */
function ListShippingNote({ item }: { item: ProductDocument }) {
    let text: string;
    if (!hasQuotableShipping(item)) {
        text = 'Shipping calculated at checkout';
    } else if (hasFreeShipping(item)) {
        text = 'Free shipping';
    } else {
        const costs = item.shipping_costs ?? [];
        const us = costs.find((c) => c.code === 'US');
        if (us && us.cost === 0) {
            text = 'Free shipping to the contiguous US';
        } else if (us) {
            text = `+ ${formatCurrency(us.cost)} shipping`;
        } else {
            text = 'Shipping calculated at checkout';
        }
    }
    return <p className="text-[12px] text-gray-600">{text}</p>;
}

/**
 * The marketplace product card: the design system's UIProductCard plus the
 * domain behaviour a design-system block can't own — JSON-LD, GA4 list
 * attribution, live auction state, sold-out and market-comparison badges.
 *
 * Use this anywhere listings are rendered; UIProductCard on its own drops the
 * schema and analytics, which are invisible in the UI but matter for SEO and
 * conversion reporting.
 */

/**
 * Width for cards inside a Carousel. Its slides are `flex w-min` with no width
 * of their own — the legacy card carried its own, so without this the new card
 * collapses to min-content. Roughly matches the width the featured grid gives a
 * card at xl (~237px).
 */
export const CAROUSEL_CARD_WIDTH = 'w-[200px] sm:w-[250px]';

// Stateful (auth, favourite count, toasts), so it can't live in the design
// system. ssr:false because liked state is per-user.
const HeartIconButton = dynamic(() => import('@c/icons/buttons/HeartButton'), {
    ssr: false,
});

interface MarketplaceProductCardProps {
    item: ProductDocument;
    /** `grid` is the vertical card; `list` is the expanded horizontal row. */
    variant?: 'grid' | 'list';
    /** Deals & Steals: show the market-comparison badge instead of the sale badge. */
    showMarketBadge?: boolean;
    priority?: boolean;
    /**
     * Open the listing in a new tab. Carousels embedded in a browsing page
     * (Staff Picks, /m/ product grids) set this so a click does not throw away
     * the shopper's place — behaviour the legacy ProductCard had and the card
     * consolidation dropped.
     */
    newTab?: boolean;
    /** Applied to the grid item, e.g. per-breakpoint visibility. */
    className?: string;
    /**
     * When true, resolve and show a "See Full Specs & Reviews" link to this
     * listing's matched GearMaster page (GB web#1005). Grid variant only —
     * list view uses a separate, uncached fetch mechanism and was never
     * wired (documented limitation carried over from the original PR).
     * Default false: this component's other callers — e.g.
     * SimilarListingsModule.tsx, rendered on the /m/ page itself — must not
     * link a listing back to the page it's already on.
     */
    showMasterLink?: boolean;
}

function MarketplaceProductCard({
    item,
    variant = 'grid',
    showMarketBadge = false,
    priority = false,
    newTab = false,
    className,
    showMasterLink = false,
}: MarketplaceProductCardProps) {
    const salePrice = useEffectivePrice(item);
    // Auction state is time-based, so defer to the client to avoid a hydration
    // mismatch — same guard the legacy card used.
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    const now = mounted ? Date.now() : 0;
    const isAuctionStarting = item.start_time ? item.start_time > now : false;
    const isAuctionEnded = item.end_time ? item.end_time < now : false;
    const timeRemaining = useCountdown(
        item.start_time ?? 0,
        item.end_time ?? 0,
        isAuctionStarting ? 'start' : 'end'
    );

    const { data: highestBid } = useQuery(
        ['highestBid', item.id],
        () => getHighestBid(item.id),
        { enabled: item.is_auction, refetchInterval: 10000 }
    );
    const bidPrice = highestBid?.bid_price ?? item.price;

    // gearMaster may be a legacy bare string rather than an array — indexing
    // a string with [0] returns its first CHARACTER, not the ID, silently
    // sending a broken lookup (same normalization ProductCard.tsx carries).
    const gearMasterArray = item.gearMaster
        ? Array.isArray(item.gearMaster)
            ? item.gearMaster
            : [item.gearMaster]
        : [];
    const matchedMasterId = gearMasterArray[0];
    const { data: matchedMaster } = useQuery(
        ['marketplaceCardMaster', matchedMasterId],
        () => getMasterById(matchedMasterId),
        {
            // Gated on the render decision too (variant !== 'list'), not just
            // showMasterLink — grid-only is a deliberate, documented scope
            // limit (list uses a separate, uncached fetch mechanism), so an
            // unscoped fetch here would fire a wasted lookup on every list-view
            // card whenever a page opts in for its grid view.
            enabled: showMasterLink && variant !== 'list' && !!matchedMasterId,
            staleTime: 60 * 60 * 1000,
        }
    );

    const listId = useListId();
    const soldOut = isSoldOut(item);

    const marketDiscount = (item as unknown as { marketDiscountPercent?: number })
        .marketDiscountPercent;
    const marketSavings = (item as unknown as { marketSavings?: number })
        .marketSavings;
    const showMarket =
        showMarketBadge && !!marketDiscount && marketDiscount >= 5 && !soldOut;

    const cardProps = toProductCardProps(item, salePrice ?? undefined);

    // An auction's live price is the highest bid, not the listed price — so it
    // has to say so. Unlabelled, the number read as an asking price next to an
    // Add-to-cart button that charged item.price instead.
    const price = item.is_auction ? bidPrice : cardProps.price;
    const priceLabel = !item.is_auction
        ? undefined
        : !mounted
          ? undefined
          : isAuctionEnded
            ? 'Winning bid'
            : highestBid
              ? 'Current bid'
              : 'Starting bid';

    // Every layer in here is decoration sitting on top of the card's link.
    // `pointer-events-none` throughout: without it the sold-out veil (inset-0)
    // swallowed every click on the image, and the auction strip killed the
    // bottom 40px of the card — both while still showing a pointer cursor, so
    // they read as clickable and did nothing.
    const overlay = (
        <>
            {showMarket && (
                <span className="pointer-events-none absolute left-[8px] top-[8px] inline-flex flex-col items-center rounded-full bg-[#059669] px-[10px] py-[4px] text-white drop-shadow-md">
                    <span className="text-[12px] font-medium leading-tight">
                        {marketDiscount}% Below Market
                    </span>
                    {!!marketSavings && marketSavings > 75 && (
                        <span className="text-[12px] font-extrabold leading-tight">
                            Save ~${Math.round(marketSavings).toLocaleString()}
                        </span>
                    )}
                </span>
            )}

            {soldOut && !item.is_auction && (
                <>
                    <span className="pointer-events-none absolute inset-0 bg-brand-black/60" />
                    <span className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-red-600 px-[16px] py-[6px] text-[13px] font-bold text-white">
                        Sold Out
                    </span>
                </>
            )}

            {item.is_auction && mounted && !isAuctionEnded && (
                <span className="pointer-events-none absolute inset-x-0 bottom-0 flex h-[40px] items-center justify-center gap-[8px] bg-gradient-orange text-white">
                    <GavelIcon />
                    <span className="whitespace-nowrap text-[13px] font-medium">
                        {isAuctionStarting ? 'Starts in' : 'Ends in'}{' '}
                        {timeRemaining.days > 0 && `${timeRemaining.days}d `}
                        {timeRemaining.hours}h {timeRemaining.minutes}m{' '}
                        {timeRemaining.seconds}s
                    </span>
                </span>
            )}

            {item.is_auction && mounted && isAuctionEnded && (
                <span className="pointer-events-none absolute inset-x-0 bottom-0 flex h-[40px] items-center justify-center gap-[8px] bg-green-500 text-white">
                    <GavelIcon />
                    <span className="whitespace-nowrap text-[14px] font-medium">
                        Sold for {formatCurrency(bidPrice)}!
                    </span>
                </span>
            )}
        </>
    );

    const hasOverlay = showMarket || soldOut || !!item.is_auction;

    const isList = variant === 'list';

    // Affirm gate (GB #1372): never advertise financing on an auction — its
    // price is a starting bid, not a buy-it-now — nor on a sold-out listing.
    // AffirmTag applies its own price-band eligibility on top and renders
    // nothing when out of band.
    //
    // Must be isSoldOut(), not `!item.out_of_stock`: sold-out is
    // `out_of_stock || stock <= 0`, so a listing with out_of_stock false and
    // stock 0 rendered the "Sold Out" overlay and a disabled buy button while
    // still advertising "as low as $X/mo" two branches away.
    const showAffirm = !soldOut && !item.is_auction;

    // Commerce slots only for the list card. freeShipping is forced off here so
    // the buyColumn's explicit ListShippingNote is the single shipping source
    // (no duplicate free-shipping line).
    const listSlots = isList
        ? {
              freeShipping: false,
              imageSlot: <ListCardCarousel product={item} />,
              shippingNote: <ListShippingNote item={item} />,
              financingSlot: showAffirm ? (
                  <AffirmTag amountUSD={price} />
              ) : undefined,
              // Never Add-to-cart on an auction: the price slot shows the
              // current bid while the cart would charge item.price, and the
              // cart conversion never checks the buyer is the winning bidder —
              // so the button let anyone buy an auction outright and skip the
              // bidding. Send them to the listing's bid flow instead.
              primaryAction: item.is_auction ? (
                  <ViewAuctionButton item={item} ended={isAuctionEnded} />
              ) : (
                  <AddToCartButton item={item} />
              ),
          }
        : {};

    // Footer slot differs by variant: list gets the buyer-protection/sell-yours
    // notes it always had; grid gets the GearMaster back-link (GB web#1005) when
    // the page opts in. Computed here (not inside listSlots) so the grid case —
    // masterLinkNote — participates in the same isList branch without list's
    // slots object needing to know about it.
    //
    // Height reserved as soon as a lookup is attempted (not only once it
    // resolves), so the card doesn't grow after paint and shift the rest of the
    // grid — the async resolution is invisible to layout even though it's still
    // async to data.
    const footerSlotNode = isList ? (
        <>
            <BuyerProtectionNote />
            <SellYoursNote />
        </>
    ) : showMasterLink && matchedMasterId ? (
        <div className="min-h-[28px]">
            {matchedMaster?.slug && (
                <Link
                    href={`/m/${matchedMaster.slug}`}
                    // noopener/noreferrer with _blank: without it the opened tab
                    // gets a window.opener handle back to this page (same
                    // convention as UIProductCard's own title link). Currently
                    // unreachable — PageMainContent never passes newTab — but a
                    // future caller that does must not inherit this gap silently.
                    {...(newTab
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : { target: '_self' })}
                    aria-label={`See full specs and reviews for ${item.title}`}
                    className="text-[12px] leading-[28px] underline hover:text-brand-primary"
                    onClick={(e) => {
                        // Navigating to /m/ is not the same listing the card's own
                        // onClick tracks — stop it from double-firing select_item
                        // for the wrong destination (same convention as
                        // BuyerProtectionNote/SellYoursNote in this file).
                        e.stopPropagation();
                        trackEvent('select_item', {
                            item_list_id: listId,
                            item_list_name: listId,
                            items: formatProductItems([item], listId),
                        });
                    }}
                >
                    See Full Specs &amp; Reviews
                </Link>
            )}
        </div>
    ) : null;

    return (
        // Single wrapper, not a fragment: these render straight into CSS grids,
        // and a fragment made ProductSchema a second grid item per product —
        // every other cell an invisible schema div, halving the visible columns.
        // h-full (grid only) keeps the card stretching to the row as it did when
        // it was the grid item itself; the list container is a flex column where
        // full height would be wrong.
        <div className={[isList ? '' : 'h-full', className].filter(Boolean).join(' ')}>
            <UIProductCard
                {...cardProps}
                {...listSlots}
                footerSlot={footerSlotNode}
                // Client-side nav instead of a full reload on every card click.
                linkComponent={Link}
                newTab={newTab}
                className={isList ? undefined : 'h-full'}
                price={price}
                priceLabel={priceLabel}
                priority={priority}
                // The market badge replaces the sale badge, as on the legacy card.
                badges={showMarket ? undefined : cardProps.badges}
                variant={variant === 'list' ? 'horizontal' : 'vertical'}
                urgency={
                    item.bumps > 0 && !showMarketBadge
                        ? { type: 'new', label: 'GearFocus Bump' }
                        : undefined
                }
                imageOverlay={hasOverlay ? overlay : undefined}
                wishlistSlot={
                    // hideCount: the card already surfaces favorite_count as
                    // "N watching".
                    <HeartIconButton
                        itemId={item.id}
                        initialCount={item.favorite_count}
                        hideCount
                    />
                }
                onClick={() =>
                    trackEvent('select_item', {
                        item_list_id: listId,
                        item_list_name: listId,
                        items: formatProductItems([item], listId),
                    })
                }
            />
            <ProductSchema product={item} pricing={salePrice ?? undefined} />
        </div>
    );
}

export default memo(MarketplaceProductCard);
